"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { useRouter } from "next/navigation";

interface User {
  email: string;
  name: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  login: (token: string, userData: User) => void;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    // Check if user is already logged in on app start
    const token = localStorage.getItem("auth-token");
    if (token) {
      try {
        // Decode the token to get user info (simple JWT decode)
        const payload = JSON.parse(atob(token.split(".")[1]));
        setUser({
          email: payload.email,
          name: "Ann", // Since we know it's Ann from the email
          role: payload.role,
        });
      } catch {
        // Invalid token, remove it
        localStorage.removeItem("auth-token");
      }
    }
    setIsLoading(false);
  }, []);

  const login = (token: string, userData: User) => {
    localStorage.setItem("auth-token", token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("auth-token");
    setUser(null);
    router.push("/");
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
